import React, { useState } from 'react';
import { useTenant } from './TenantContext';
import { useFAQEntityByName } from '../hooks/useFAQEntityByName';
import { Attribute as FAQAttribute } from '../types';
import { INLINE_LINK_CLASS, FAFSA_URL } from '../utils/linkUtils';

export interface WhatYouShouldKnowProps {
    /**
     * The specific name of the FAQ entity to fetch and display.
     * e.g., "Welcome - FAQ", "ParentFinances - FAQ"
     */
    faqName: string;
    /**
     * Optional additional CSS classes to apply to the root container.
     */
    className?: string;
}

/**
 * A modular and accessible FAQ accordion component that fetches and displays
 * a single FAQ entity based on the provided `faqName`.
 * It handles its own loading and error states.
 *
 * @example
 * ```tsx
 * <WhatYouShouldKnow faqName="Welcome - FAQ" />
 * ```
 */
const WhatYouShouldKnow: React.FC<WhatYouShouldKnowProps> = ({
    faqName,
    className = '',
}) => {
    const [openIndex, setOpenIndex] = useState<number | null>(null);
    const { tenantConfig } = useTenant();
    const backgroundColor = tenantConfig?.authority?.authorityBranding?.primaryColor;
    const { faqAttributes, isLoading } = useFAQEntityByName(faqName);

    const handleAccordionToggle = (idx: number) => {
        setOpenIndex(openIndex === idx ? null : idx);
    };

    return (
        <div className={`border-2 border-gray-600 rounded-lg overflow-hidden ${className}`}>
            <div
                className="text-white px-4 py-2 font-semibold"
                style={{ backgroundColor }}
            >
                Common Questions
            </div>
            {isLoading && (
                <output aria-live="polite" className="text-gray-600">
                    Loading FAQ...
                </output>
            )}
            {!isLoading && faqAttributes.length === 0 && (
                <div role="alert" aria-live="assertive" className="text-red-600">
                    Error: FAQ data not available for "{faqName}"
                </div>
            )}
            {!isLoading && faqAttributes.length > 0 && (
                <div className="space-y-6 pb-4">
                    {faqAttributes.map((attr: FAQAttribute, idx: number) => {
                        const headingId = `faq-heading-${attr._id || idx}`;
                        const panelId = `faq-panel-${attr._id || idx}`;
                        return (
                            <div key={attr._id || idx} className={`border-b last:border-b-0 ${idx === 0 ? 'mt-6' : ''}`}>
                                <button
                                    type="button"
                                    onClick={() => handleAccordionToggle(idx)}
                                    aria-expanded={openIndex === idx}
                                    aria-controls={panelId}
                                    className="flex justify-between items-center w-full px-4 text-left font-medium text-gray-800 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-blue-500"
                                    id={headingId}
                                >
                                    <span>{attr.name}</span>
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        className={`h-5 w-5 transform transition-transform duration-200 ${openIndex === idx ? 'rotate-180' : ''}`}
                                        viewBox="0 0 20 20"
                                        fill="currentColor"
                                        aria-hidden="true"
                                    >
                                        <path
                                            fillRule="evenodd"
                                            d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                                            clipRule="evenodd"
                                        />
                                    </svg>
                                </button>
                                {openIndex === idx && (
                                    <section
                                        id={panelId}
                                        aria-labelledby={headingId}
                                        className="px-4 pb-4 bg-gray-50 text-gray-600"
                                    >
                                        {attr.possibleValues && (
                                            <div
                                                dangerouslySetInnerHTML={{
                                                    __html: attr.possibleValues.replace(
                                                        /Free Application for Federal Student Aid(\s*\(FAFSA®?\))?/gi,
                                                        `<a href="${FAFSA_URL}" target="_blank" rel="noopener noreferrer" class="${INLINE_LINK_CLASS}">$&</a>`
                                                    )
                                                }}
                                            />
                                        )}
                                    </section>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default WhatYouShouldKnow;
